import React, { useContext, useEffect } from 'react';
import cx from 'clsx';
import { FormattedMessage } from 'react-intl';
import {
  useMatchesState, usePricesMachineContext, useSelectorMachineContext, VatModeContext,
} from '@printdeal/product-selector';
import { Experiment } from '@printdeal/feature-management/react';
import { isCustomerProgramReseller, useSegmentation } from '@printdeal/segmentation/react';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { PriceInfo } from './PriceInfo';
import { Money } from '../../../../Money';

interface Props extends React.ComponentPropsWithoutRef<'section'> {
  customerGroupId?: string;
}

const PriceSection = ({ customerGroupId, className }: Props) => {
  const { vatMode } = useContext(VatModeContext);
  const pricesLoading = useMatchesState({ STARTED: { 'FETCHING DATA': { PRICES: 'FETCHING PRICES' } } });
  const servicePricesLoading = useMatchesState({ STARTED: { 'FETCHING DATA': { 'SERVICE PRICES': 'FETCHING SERVICE PRICES' } } });
  const hasInsufficientInventory = useMatchesState({ STARTED: { 'FETCHING DATA': { 'ARRIVAL DATES': 'INSUFFICIENT INVENTORY' } } });
  const isLoading = pricesLoading || servicePricesLoading;
  const totalPrice = useSelectorMachineContext((state) => state.context.totalPrice);
  const selectedPrice = usePricesMachineContext((state) => state.context.selectedPrice);
  const selectionMode = useSelectorMachineContext((state) => state.context.selectionMode);
  const isStepByStep = selectionMode === 'step-by-step';
  const discount = (selectedPrice && selectedPrice.price && selectedPrice.discountedPrice)
    ? selectedPrice.price[vatMode].centAmount - selectedPrice.discountedPrice[vatMode].centAmount : 0;
  const { data: segmentationData } = useSegmentation();
  const isResellerPartOfCustomerProgram = isCustomerProgramReseller(
    segmentationData?.isReseller, segmentationData?.isPartOfCustomerProgram,
  );
  const isErrorLoadingPrices = useMatchesState({ STARTED: { 'FETCHING DATA': { PRICES: 'ERRORING' } } });

  useEffect(() => {
    if (hasInsufficientInventory) {
      dispatchDataLayerEntry(
        'selector',
        'warning',
        {
          msg: 'Insufficient_inventory',
        },
      );
    }
  }, [hasInsufficientInventory]);

  return (
    <section className={cx('pd-my-5', className)}>
      <div className={cx({ 'pd-animate-pulse': isLoading })}>
        { customerGroupId && discount > 0 && isResellerPartOfCustomerProgram && (
          <Experiment id="exp1615-rerun_reseller_discount_pdp_summary_and_checkout">
            <Experiment.Variant>
              <div className="pd-mt-4 pd-text-right pd-text-dim-500">
                <span className="pd-mr-1">
                  { '- ' }
                  <Money value={discount} currency={totalPrice?.[vatMode]?.currencyCode} />
                </span>
                <span className="pd-ml-2">
                  <FormattedMessage id="partner.pro.discount" defaultMessage="Partner pro discount" />
                </span>
              </div>
            </Experiment.Variant>
          </Experiment>
        )}
        {isErrorLoadingPrices && (
          <div className="pd-flex pd-items-center pd-justify-end">
            <span data-cy="price-error" className={cx('pd-mt-3 pd-mr-2 pd-font-bold')}>
              <FormattedMessage
                id="general.error.pricesNotLoading"
                defaultMessage="Prices not available at the moment. Please try again later"
              />
            </span>
          </div>
        )}
        {!isErrorLoadingPrices && (
        <>
          <PriceInfo
            centAmount={totalPrice?.totalNet?.centAmount}
            currencyCode={totalPrice?.totalNet?.currencyCode}
            classNames={cx('pd-mt-3', 'pd-mr-2', { 'pd-text-dim-500': vatMode === 'totalGross' })}
            isStepByStep={isStepByStep}
            isLoading={isLoading}
            vatMode="totalNet"
          />
          <PriceInfo
            centAmount={totalPrice?.totalGross?.centAmount}
            currencyCode={totalPrice?.totalGross?.currencyCode}
            classNames={cx('pd-mt-4', 'pd-mr-2', { 'pd-text-dim-500': vatMode === 'totalNet' })}
            isStepByStep={isStepByStep}
            isLoading={isLoading}
            vatMode="totalGross"
          />
        </>
        )}
      </div>

      {process.env.GATSBY_STORE === 'printdeal-be' && (
        <div className="pd-mt-4 pd-flex pd-items-center pd-justify-end pd-font-semibold pd-text-success-500">
          <FormattedMessage
            id="free.shipping.costs"
            defaultMessage="Free shipping within the Netherlands and Belgium"
          />
        </div>
      )}
    </section>
  );
};

export default PriceSection;
